import { CustomUseQueryOptions } from '../types';
import { hasNonEmptyProp } from '@/libs/common/utils';
import type { ApiInstance, MarketDetail, MarketDetailParams } from '@/libs/sdk/api';
import { useSSEQuery } from '../hooks/use-sse-query';

/** Query type of the market detail read; the params object follows it at key[2]. */
export const MARKET_DETAIL_QUERY = 'MarketDetail';

export type UseMarketDetailOptions = CustomUseQueryOptions<MarketDetail> & {
  subscribe?: boolean;
};

// Single source of truth for the MarketDetail cache entry (key shape + queryFn).
// Subscription-free fan-outs (e.g. the event page useQueries) must reuse this so
// they share useMarketDetail's cache entries and receive its SSE/trade-driven
// invalidations instead of creating duplicate entries under a divergent key.
export const marketDetailQueryOptions = (
  api: ApiInstance,
  searchParams: MarketDetailParams,
  options?: Pick<UseMarketDetailOptions, 'queryKey' | 'enabled'>
) => {
  const { queryKey, enabled = true } = options || {};
  return {
    enabled: enabled && hasNonEmptyProp(searchParams, ['marketAddress']),
    queryKey: [queryKey, MARKET_DETAIL_QUERY, searchParams],
    queryFn: async () => {
      const { data } = await api.market.getMarketDetail(searchParams!);
      return data;
    },
  };
};

export const useMarketDetail = (
  api: ApiInstance,
  searchParams: MarketDetailParams,
  options?: UseMarketDetailOptions
) => {
  const { marketAddress } = searchParams || {};
  const { queryKey, enabled = true, subscribe = true, ...rest } = options || {};

  return useSSEQuery(
    api.sse,
    {
      ...rest,
      ...marketDetailQueryOptions(api, searchParams, { queryKey, enabled }),
    },
    subscribe
      ? [
          { event: 'market:price_change', topics: [marketAddress] },
          { event: 'market:info_update', topics: [marketAddress] },
        ]
      : [],
    500 // reduced debounce for more responsive market detail updates
  );
};
